/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {DialogModule, ToastModule, TS_Checkbox, TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {GenericSelect} from "@intuitionrobotics/thunderstorm/app-frontend/components/GenericSelect";
import {DB_Patient} from "@app/app-shared/patient";
import React, {useContext, useState} from "react";
import {inputTextField, margin_tb_lg, padding_rl_lg, padding_t_med, Spacing} from "@styles/styles";
import {currentTimeMillies, filterDuplicates, generateHex} from "@intuitionrobotics/ts-common";
import * as emotion from "emotion";
import dayjs from "../../../utils/dayjs";
import {AppointmentModule} from "@modules/AppointmentModule";
import {ICONS} from "@res/icons";
import {COLORS} from "@res/colors";
import {selectStyles} from "../SelectStyle";
import {RemindersModule} from "@modules/RemindersModule";
import {FONTS} from "@res/fonts";
import {IconInfo} from "../../widget/IconInfo";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {DateTimePicker} from "../patients/DateTimePicker";
import {OKButton} from "../../widget/OKButton";
import {InPlaceLoader} from "../../widget/InPlaceLoader";
import {CardHeader} from "../../widget/CardHeader";
import {HLine} from "../../widget/HLine";
import {HGap} from "../../widget/HGap";
import {OutlineButton} from "../../widget/OutlineButton";
import {CreateCalendarEvent} from "@app/ir-q-app-common/types/calendar_event";
import {OrganizationsContext} from "../organizations/OrganizationsContext";
import {ContactsContext} from "../messages/ContactsContext";
import {PatientsContext} from "../patients/PatientsContext";


const inputText = emotion.css(
    {
        marginBottom: 15,
        ':placeholder': {
            fontFamily: "Inter-Regular",
            fontSize: 14
        }
    }
);

const inputTextLabel = emotion.css(
    {
        marginBottom: 5
    }
);

const checkBoxWithText = emotion.css(
    {
        marginBottom: "5px",
        display: "flex",
        flexDirection: "row"
    }
)

type Props = {
    patient: DB_Patient;
}

enum MedicationReminderDialogUI {
    Creating,
    ConfirmationClose
}

type ReminderData = {
    medReminderName: string;
    timestamps: number[];
    isRecurring: boolean;
    requiresMeal: boolean;
    recurrence: string;
    isCriticalMedication: boolean;
}

const options = ['daily', 'weekly'];

export const MedicationReminderDialog = (props: Props) => {
    const [loading, setLoading] = React.useState(false);
    const [ui, setUi] = React.useState(MedicationReminderDialogUI.Creating);
    const [uiKey, setUiKey] = useState(generateHex(8))
    const [medReminderName, setMedReminderName] = React.useState('');
    const [requiresMeal, setRequiresMeal] = React.useState(false);
    const [isRecurring, setIsRecurring] = React.useState(false);
    const [recurrence, setRecurrence] = React.useState('');
    const [timestamps, setTimestamps] = React.useState<number[]>([]);
    const [isCriticalMedication, setIsCriticalMedication] = React.useState(false);
    const orgCtx = React.useContext(OrganizationsContext);
    const contactsContext = React.useContext(ContactsContext);
    const {demo} = useContext(PatientsContext)
    const [reminderDataList, setReminderDataList] = useState<ReminderData[]>([]);

    const dates = AppointmentModule.getMinMaxDateForAppointments();
    const minDate: Date = dates.minDate.toDate();
    const maxDate: Date = dates.maxDate.endOf("day").toDate();

    const resetState = () => {
        setMedReminderName('');
        setRequiresMeal(false);
        setIsRecurring(false);
        setRecurrence('');
        setTimestamps([]);
        setIsCriticalMedication(false);
    }

    /**
     * Validate the fields before creating an event
     * we don't allow for empty reminder name, timestamp or a recurring event with no frequency
     */
    const validateFields = (): boolean => {
        if (ui !== MedicationReminderDialogUI.Creating)
            return true;

        if (!medReminderName.trim())
            return false
        if (!timestamps.length)
            return false
        if (!recurrence.length && isRecurring)
            return false
        if (isRecurring && !!recurrence.trim())
            return true;

        return !timestamps.some(_timestamp => dayjs(_timestamp).tz(props.patient.timezone).isBefore(currentTimeMillies()));
    }

    const renderContent = () => {
        switch (ui) {
            case MedicationReminderDialogUI.ConfirmationClose:
                return <div className={`ll_v_c`}>
                    {`Are you sure you want to remove your previous ${getLength()} medication reminders?`}
                </div>
            case MedicationReminderDialogUI.Creating:
                return renderCreatingContent();
        }
    };

    const renderCreatingContent = () => <div className={`ll_v_c`} style={{
        height: "50vh"
    }}>
        <form>
            <div className={`${inputText} ll_v_l`}>
                <div className={inputTextLabel}>
                    {FONTS.reg_16_black(`Medication Reminder Name`)}
                </div>
                <div style={{width: "100%"}}>
                    <TS_Input<string>
                        className={inputTextField}
                        id={"reminder-name"}
                        value={medReminderName}
                        placeholder={"eg. blood sugar medication"}
                        onChange={(value: string) => setMedReminderName(value)}
                        type={"text"}
                        name={generateHex(8)}
                        spellCheck={true}
                        style={{width: "inherit"}}
                    />
                </div>
            </div>
            <DateTimePicker
                _key={uiKey}
                minDate={minDate}
                maxDate={maxDate}
                allowMultipleTimes={true}
                initialTimes={timestamps.map(_timestamp => new Date(_timestamp))}
                onChange={(_dates) => {
                    if (!_dates)
                        return;

                    setTimestamps(_dates.map(item => new Date(item).getTime()).sort());
                }}

            />
            <div className={checkBoxWithText}>
                <TS_Checkbox
                    value={isRecurring}
                    checked={isRecurring}
                    label={""}
                    onCheck={(_, checked) => {
                        setIsRecurring(!checked)
                        setRecurrence('');
                    }}
                />
                <div style={{paddingTop: "4px"}}>
                    {FONTS.reg_16_black(`Repeat`)}
                </div>
                <div style={{marginLeft: "5px"}}>
                    <GenericSelect<string>
                        isDisabled={!isRecurring}
                        options={options}
                        selectedOption={recurrence}
                        iconClose={ICONS.triangle_down(COLORS.grays_4, 12)}
                        iconOpen={ICONS.triangle_up(COLORS.grays_4, 12)}
                        onChange={value => setRecurrence(value)}
                        styles={selectStyles}
                        presentation={(p) => p}
                    />
                </div>
            </div>
            <div className={checkBoxWithText}>
                <TS_Checkbox
                    value={requiresMeal}
                    checked={requiresMeal}
                    label={''}
                    onCheck={(_, checked) => {
                        setRequiresMeal(!checked);
                    }}
                />
                {FONTS.reg_16_black(`Take with food`)}
            </div>
            <div className={checkBoxWithText}>
                <TS_Checkbox
                    value={isCriticalMedication}
                    checked={isCriticalMedication}
                    label={''}
                    onCheck={(_, checked) => {
                        setIsCriticalMedication(!checked);
                    }}
                />
                {FONTS.reg_16_black(`Critical Medication`)}
                <IconInfo
                    onMouseOver={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        showTooltip(
                            e, FONTS.reg_14_secondary_dark(`Check this box if you want to be notified\
                                     when this medication was not taken`),
                            rect.left,
                            rect.top,
                            {
                                zIndex: 9999,
                                backgroundColor: COLORS.white,
                                padding: 8,
                                width: 300,
                                transform: "translate(0%, -105%)",
                                border: `1px solid ${COLORS.primary_light_blue}`
                            });
                    }}
                    onMouseLeave={() => {
                        hideTooltip();
                    }}/>
            </div>
        </form>
    </div>;


    const renderXButton = () => {
        if (ui === MedicationReminderDialogUI.ConfirmationClose)
            return null;

        const xSize = 9;
        return <div className={'x-btn-wrapper'}>
            <div className={'clickable'}
                 style={{padding: Spacing.s, width: xSize, lineHeight: '0px'}}
                 onClick={() => onClose()}
            >
                {ICONS.close(COLORS.neutral_gray_1, xSize)}
            </div>
        </div>;
    };

    const renderHeader = () => {
        return <>
            <CardHeader>
                {FONTS.medium_18_blue(getTitle())}
            </CardHeader>
            <HLine style={{marginTop: Spacing.s}}/>
        </>;
    };

    const renderFooter = () => {
        return <div className={`ll_h_c justify-end ${padding_rl_lg} ${margin_tb_lg}`}>
            {renderSaveCancel()}
        </div>;
    };

    const renderBody = () => {
        return <div className={`${padding_rl_lg} ${padding_t_med}`} style={{display: 'flex', flex: 1, maxHeight: '70vh'}}>
            <div className={'ll_v_s'} style={{flexGrow: 1, minHeight: 'min-content', position: 'relative'}}>
                {renderContent()}
            </div>
        </div>;
    };

    const getTitle = (): string => {
        switch (ui) {
            case MedicationReminderDialogUI.ConfirmationClose:
                return "Cancel medication reminder creation";
            case MedicationReminderDialogUI.Creating:
                return "Create a medication reminder";
        }
    };


    const renderSaveCancel = () => {
        const saveEnabled = validateFields();
        switch (ui) {
            case MedicationReminderDialogUI.Creating:
                const length = getLength();
                return <div className={'ll_h_c'}>
                    <HGap h={Spacing.m}/>
                    <OutlineButton
                        text={"Create Another"}
                        textStyle={{whiteSpace: 'nowrap'}}
                        enabled={saveEnabled}
                        onClick={() => onCancel()}
                    />
                    <HGap h={Spacing.m}/>
                    <OKButton
                        text={`Send${length ? ` (${length})` : ""}`}
                        textStyle={{whiteSpace: 'nowrap'}}
                        enabled={saveEnabled}
                        onClick={() => onSave()}
                    />
                </div>;

            case MedicationReminderDialogUI.ConfirmationClose:
                return <div className={'ll_h_c'}>
                    <OutlineButton
                        text={"Go Back"}
                        textStyle={{whiteSpace: 'nowrap'}}
                        onClick={() => setUi(MedicationReminderDialogUI.Creating)}
                    />
                    <HGap h={Spacing.m}/>
                    <OKButton
                        text={'Remove this'}
                        enabled={true}
                        textStyle={{whiteSpace: 'nowrap'}}
                        onClick={() => removeLast()}
                    />
                    {reminderDataList.length && <>
                        <HGap h={Spacing.m}/>
                        <OKButton
                            text={"Remove All"}
                            textStyle={{whiteSpace: 'nowrap'}}
                            enabled={true}
                            onClick={() => onSave()}
                        /></>}
                </div>;
        }
    };

    const getLength = () => getReminderLength() + timestamps.length;
    const getReminderLength = (): number => reminderDataList.reduce((s, i) => s + i.timestamps.length, 0);

    const onClose = () => {
        if (!reminderDataList.length)
            return DialogModule.close();

        setUi(MedicationReminderDialogUI.ConfirmationClose);
        return true;
    };

    const onCancel = () => {
        switch (ui) {
            case MedicationReminderDialogUI.ConfirmationClose:
                setUi(MedicationReminderDialogUI.Creating)
                return true;
            case MedicationReminderDialogUI.Creating:
                const body = getEventsFromForm();
                if (!body)
                    return true;

                setReminderDataList(prevReminderDataList => {
                    return [...prevReminderDataList, {
                        medReminderName: medReminderName,
                        timestamps: timestamps,
                        isRecurring: isRecurring,
                        requiresMeal: requiresMeal,
                        recurrence: recurrence,
                        isCriticalMedication: isCriticalMedication
                    }]
                });
                setUiKey(generateHex(8))
                resetState();
                return true;
        }
    };


    const onSave = () => {
        switch (ui) {
            case MedicationReminderDialogUI.ConfirmationClose:
                return DialogModule.close();

            case MedicationReminderDialogUI.Creating:
                if (demo) {
                    ToastModule.toastSuccess(`The medication reminder has been sent for approval by the user`)
                    // When the reminder is created we close the dialog
                    DialogModule.close();
                    return false;
                }

                const body = getEventsFromForm();

                if (!body)
                    return true;


                const finalList: CreateCalendarEvent[] = []
                //Add prev events
                reminderDataList.forEach(reminder => {
                    const org = orgCtx.organizations[props.patient.organizationId];
                    const generateReminderBody = RemindersModule.generateReminderBody(
                        contactsContext.contacts,
                        org,
                        reminder.medReminderName,
                        // the date of the reminder can be any of the timestamps provided
                        reminder.timestamps,
                        reminder.requiresMeal,
                        reminder.isRecurring ? RemindersModule.convertToRfcDates(
                            reminder.recurrence as "daily" | "weekly" | "",
                            reminder.timestamps[0]) : "",
                        reminder.isCriticalMedication
                    );
                    if (generateReminderBody)
                        finalList.push(...generateReminderBody);
                })
                finalList.push(...body);

                RemindersModule.createReminder(finalList, props.patient)
                    .then(() => {
                        ToastModule.toastSuccess(`The medication reminder${finalList.length > 1 ? "s have" : " has"} been sent for approval by the user`)
                        // When the reminder is created we close the dialog
                        DialogModule.close();

                        RemindersModule.fetchRemindersForPatient(contactsContext.contacts, orgCtx.organizations[props.patient.organizationId], props.patient);
                    }).catch(() => {
                        setLoading(false)
                    })

                setLoading(true);
                resetState();

                return true;
        }
    };

    const getEventsFromForm = () => {
        if (timestamps.length === 0) {
            ToastModule.toastError('Please fill in the time of the reminder')
            return;
        }
        if (!isRecurring && timestamps.some(_timestamp => _timestamp < currentTimeMillies())) {
            ToastModule.toastError('Please input a valid time')
            return;
        }

        const uniqueTimes = filterDuplicates(timestamps);

        if (uniqueTimes.length !== timestamps.length) {
            ToastModule.toastError('You input the same time twice, please delete or edit the duplicate entry')
            return;
        }

        const org = orgCtx.organizations[props.patient.organizationId];
        return RemindersModule.generateReminderBody(
            contactsContext.contacts,
            org,
            medReminderName,
            // the date of the reminder can be any of the timestamps provided
            timestamps,
            requiresMeal,
            isRecurring ? RemindersModule.convertToRfcDates(
                recurrence as "daily" | "weekly" | "",
                timestamps[0]) : "",
            isCriticalMedication
        );
    }

    const removeLast = () => {
        const popped = reminderDataList.pop();
        if (!popped)
            return DialogModule.close();

        setReminderDataList([...reminderDataList])
        setMedReminderName(popped.medReminderName);
        setTimestamps(popped.timestamps);
        setIsRecurring(popped.isRecurring);
        setRequiresMeal(popped.requiresMeal);
        setRecurrence(popped.recurrence);
        setIsCriticalMedication(popped.isCriticalMedication);
        setUi(MedicationReminderDialogUI.Creating)
    }

    return <div className={`ll_v_s`} style={{position: 'relative'}}>
        <InPlaceLoader loading={loading}/>
        {renderXButton()}
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
    </div>
}
